import { useId } from 'react';

import type { DocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { orderBy } from 'lodash';

import type { OfficeType, OptionsType } from '../../../Data/types';

type Option = OptionsType | OfficeType;

const getOptionName = (option: Option | null | undefined): string =>
  (option as OptionsType)?.name || (option as OfficeType)?.name || '';

const getOptionId = (option: Option | null | undefined): string | number =>
  (option as OptionsType)?.id || (option as OfficeType)?.id || '';

type Props = {
  query?: DocumentNode;
  placeholder: string;
  optionValueKey: string;
  multiple?: boolean;
  limitTags?: number;
  value?: OptionsType[] | OptionsType | OfficeType[] | OfficeType | null;
  onChange: (
    value:
      | OptionsType
      | OptionsType[]
      | OfficeType
      | OfficeType[]
      | (OptionsType | OptionsType[] | OfficeType | OfficeType[] | null)[]
      | null,
  ) => void;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
};

function DropdownSelect({
  value,
  onChange,
  query,
  placeholder,
  optionValueKey,
  multiple,
  limitTags,
  required,
  disabled,
  readOnly,
  error,
}: Readonly<Props>) {
  const { data, loading } = useQuery(query as DocumentNode);
  const queryData = data as Record<string, unknown[]> | undefined;
  const id = useId();

  let options: OfficeType[] | OptionsType[];
  if (optionValueKey === 'gec_offices') {
    const rawOffices = (queryData?.gec_offices ?? []) as {
      id: string;
      office_code: string;
      name: string;
      address?: string | null;
    }[];
    options = orderBy(
      rawOffices.map((office) => ({
        id: office.id,
        office_code: office.office_code,
        name: office.name,
        address: office.address ?? '',
      })),
      [
        // custom sort priority: Firmwide first, Virtual second, then everything else
        (office) => {
          if (office.office_code === 'FRM') return 0;
          if (office.office_code === 'VIR') return 1;
          return 2;
        },
        // then alphabetize by name
        (office) => office.name?.toLowerCase(),
      ],
      ['asc', 'asc'],
    );
  } else if (optionValueKey === 'gec_event_type') {
    const rawEventTypes = (queryData?.gec_event_type ?? []) as { id: number; event_type: string }[];
    options = orderBy(
      rawEventTypes.map((eventType) => ({
        id: eventType.id,
        name: eventType.event_type,
      })),
      [(type) => (type.name === 'Other' ? 1 : 0), (type) => type.name?.toLowerCase()],
      ['asc', 'asc'],
    );
  } else {
    const rawOptions = (queryData?.[optionValueKey] ?? []) as { id: number; name: string }[];
    options = orderBy(
      rawOptions.map((option) => ({
        id: option.id,
        name: option.name,
      })),
      [(type) => type.name?.toLowerCase()],
      ['asc', 'asc'],
    );
  }

  const labelColor = (key: string) => {
    switch (key) {
      case 'gec_practice_groups_rollup':
        return '#79D92F'; // Green
      case 'gec_industries':
        return '#93D8F7'; // Blue
      case 'gec_firm_initiatives':
        return '#FF8B2A'; // Orange
      case 'gec_departments':
        return '#DFC5A2'; // Red
      default:
        return undefined;
    }
  };
  return (
    <Autocomplete
      multiple={multiple}
      size="small"
      fullWidth
      loading={loading}
      limitTags={limitTags}
      id={id}
      options={options ?? []}
      getOptionLabel={(option) => getOptionName(option)}
      defaultValue={multiple ? [] : null}
      value={value || (multiple ? [] : null)}
      onChange={(_event, value) => {
        onChange(value);
      }}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      renderValue={
        multiple
          ? (value, getItemProps) =>
              Array.isArray(value)
                ? (value as Option[]).map((option, index) => (
                    <Chip
                      label={getOptionName(option)}
                      size="small"
                      sx={{
                        backgroundColor: labelColor(optionValueKey),
                        color: labelColor(optionValueKey) ? 'black' : 'inherit',
                        height: 'auto',
                        '& .MuiChip-label': {
                          display: 'block',
                          whiteSpace: 'normal',
                        },
                      }}
                      {...getItemProps({ index })}
                      key={`${getOptionId(option)}-${index}`}
                    />
                  ))
                : null
          : undefined
      }
      disabled={disabled}
      readOnly={readOnly}
      renderInput={(params) => <TextField {...params} placeholder={placeholder} required={required} error={error} />}
    />
  );
}

export default DropdownSelect;
